import React, { useEffect, useState } from 'react'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Checkbox from '@material-ui/core/Checkbox'
import MenuItem from '@material-ui/core/MenuItem'
import { t } from '../i18n'
import {
  csvToList,
  listToCsv,
  createEmptyProtocol,
  cloneProtocol,
  normalizeProtocol,
  mergeHangingCatalog,
  isSeedOnlyProtocol,
} from '../utils/hangingCatalog'

const fieldSx = { marginBottom: 10, width: '100%' }

/**
 * Editor hanging (sesión): clonar/crear, editar reglas metadatos, aplicar, guardar memoria.
 */
function HangingProtocolEditor({
  open,
  customProtocols,
  initialProtocolId,
  onClose,
  onSaveCustom,
  onDeleteCustom,
  onApply,
}) {
  const catalog = mergeHangingCatalog(customProtocols)
  const [selectedId, setSelectedId] = useState(initialProtocolId || '')
  const [draft, setDraft] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return
    const id = initialProtocolId || (catalog[0] && catalog[0].id) || ''
    setSelectedId(id)
    const proto =
      catalog.find((p) => p.id === id) || createEmptyProtocol()
    setDraft(JSON.parse(JSON.stringify(normalizeProtocol(proto) || createEmptyProtocol())))
    setError('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialProtocolId])

  const selectProtocol = (id) => {
    setSelectedId(id)
    const proto = catalog.find((p) => p.id === id)
    if (!proto) return
    setDraft(JSON.parse(JSON.stringify(normalizeProtocol(proto))))
    setError('')
  }

  const patchDraft = (partial) => {
    setDraft((prev) => (prev ? { ...prev, ...partial } : prev))
  }

  const buildNormalizedFromDraft = () => {
    if (!draft) return null
    const modalities = csvToList(
      draft._modCsv != null
        ? draft._modCsv
        : listToCsv((draft.match && draft.match.modalities) || [])
    )
    const studyDescription = csvToList(
      draft._studyDescCsv != null
        ? draft._studyDescCsv
        : listToCsv((draft.match && draft.match.studyDescription) || [])
    )
    const slots = (draft.slots || []).map((slot, i) => {
      const series = { ...(slot.series || {}) }
      series.description = csvToList(
        draft.slots[i]._descCsv != null
          ? draft.slots[i]._descCsv
          : listToCsv(series.description || [])
      )
      series.exclude = csvToList(
        draft.slots[i]._exclCsv != null
          ? draft.slots[i]._exclCsv
          : listToCsv(series.exclude || [])
      )
      const clean = { ...slot, series }
      delete clean._descCsv
      delete clean._exclCsv
      return clean
    })
    return normalizeProtocol({
      ...draft,
      match: { modalities, studyDescription },
      slots,
    })
  }

  const patchSlot = (index, partial) => {
    setDraft((prev) => {
      if (!prev) return prev
      const slots = (prev.slots || []).slice()
      const cur = { ...(slots[index] || {}) }
      slots[index] = { ...cur, ...partial }
      return { ...prev, slots }
    })
  }

  const patchSlotSeries = (index, partial) => {
    setDraft((prev) => {
      if (!prev) return prev
      const slots = (prev.slots || []).slice()
      const cur = { ...(slots[index] || {}) }
      cur.series = { ...(cur.series || {}), ...partial }
      slots[index] = cur
      return { ...prev, slots }
    })
  }

  const handleNew = () => {
    const p = createEmptyProtocol()
    setDraft(p)
    setSelectedId(p.id)
    setError('')
  }

  const handleClone = () => {
    if (!draft) return
    const c = cloneProtocol(draft)
    if (!c) return
    setDraft(c)
    setSelectedId(c.id)
    setError('')
  }

  const handleSave = () => {
    const saved = buildNormalizedFromDraft()
    if (!saved) {
      setError(t('hanging.editorInvalid'))
      return null
    }
    if (typeof onSaveCustom === 'function') onSaveCustom(saved)
    setDraft(saved)
    setSelectedId(saved.id)
    setError('')
    return saved
  }

  const handleDelete = () => {
    if (!draft || !draft.id) return
    if (isSeedOnlyProtocol(draft.id, customProtocols)) {
      setError(t('hanging.editorCannotDeleteSeed'))
      return
    }
    if (typeof onDeleteCustom === 'function') onDeleteCustom(draft.id)
    handleNew()
  }

  const handleApply = () => {
    const saved = handleSave()
    if (!saved) return
    if (typeof onApply === 'function') onApply(saved)
  }

  const addSlot = () => {
    setDraft((prev) => {
      if (!prev) return prev
      const slots = (prev.slots || []).slice()
      const idx = slots.length
      const rows = (prev.layout && prev.layout[0]) || 1
      const cols = (prev.layout && prev.layout[1]) || 1
      slots.push({
        viewportIndex: Math.min(idx, rows * cols - 1),
        role: 'current',
        series: {
          modality: (prev.match && prev.match.modalities && prev.match.modalities[0]) || 'CT',
          description: [],
          exclude: ['scout', 'ref', 'localizer'],
          requireDifferentPlane: idx > 0,
        },
      })
      return { ...prev, slots }
    })
  }

  const removeSlot = (index) => {
    setDraft((prev) => {
      if (!prev || !(prev.slots || []).length) return prev
      const slots = prev.slots.filter((_, i) => i !== index)
      return { ...prev, slots }
    })
  }

  if (!draft) return null

  const modCsv =
    draft._modCsv != null
      ? draft._modCsv
      : listToCsv((draft.match && draft.match.modalities) || [])
  const studyCsv =
    draft._studyDescCsv != null
      ? draft._studyDescCsv
      : listToCsv((draft.match && draft.match.studyDescription) || [])

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          background: '#1a2332',
          color: '#e8e6e3',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle style={{ borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
        {t('hanging.editorTitle')}
      </DialogTitle>
      <DialogContent style={{ paddingTop: 16 }}>
        <p style={{ fontSize: 12, opacity: 0.85, marginTop: 0 }}>
          {t('hanging.editorHint')}
        </p>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(160px, 28%) 1fr',
            gap: 16,
            alignItems: 'start',
          }}
        >
          <div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
              <Button size="small" variant="outlined" color="primary" onClick={handleNew}>
                {t('hanging.editorNew')}
              </Button>
              <Button size="small" variant="outlined" onClick={handleClone}>
                {t('hanging.editorClone')}
              </Button>
            </div>
            <div
              style={{
                maxHeight: 360,
                overflow: 'auto',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 6,
              }}
            >
              {catalog.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => selectProtocol(p.id)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 10px',
                    border: 'none',
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                    background:
                      selectedId === p.id
                        ? 'rgba(100,181,246,0.25)'
                        : 'transparent',
                    color: '#e8e6e3',
                    cursor: 'pointer',
                    fontSize: 12,
                  }}
                >
                  <div style={{ fontWeight: 700 }}>{p.label}</div>
                  <div style={{ opacity: 0.7, fontSize: 10 }}>
                    {p.id} · {p._source === 'session' ? 'session' : 'seed'}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <TextField
              label={t('hanging.editorId')}
              value={draft.id || ''}
              onChange={(e) => patchDraft({ id: e.target.value })}
              variant="outlined"
              size="small"
              style={fieldSx}
              InputProps={{ style: { color: '#fff' } }}
              InputLabelProps={{ style: { color: '#aaa' } }}
            />
            <TextField
              label={t('hanging.editorLabel')}
              value={draft.label || ''}
              onChange={(e) => patchDraft({ label: e.target.value })}
              variant="outlined"
              size="small"
              style={fieldSx}
              InputProps={{ style: { color: '#fff' } }}
              InputLabelProps={{ style: { color: '#aaa' } }}
            />
            <TextField
              label={t('hanging.editorPriority')}
              type="number"
              value={draft.priority != null ? draft.priority : 50}
              onChange={(e) =>
                patchDraft({ priority: Number(e.target.value) || 0 })
              }
              variant="outlined"
              size="small"
              style={fieldSx}
              InputProps={{ style: { color: '#fff' } }}
              InputLabelProps={{ style: { color: '#aaa' } }}
            />
            <TextField
              label={t('hanging.editorModalities')}
              helperText={t('hanging.editorCsvHint')}
              value={modCsv}
              onChange={(e) => patchDraft({ _modCsv: e.target.value })}
              variant="outlined"
              size="small"
              style={fieldSx}
              InputProps={{ style: { color: '#fff' } }}
              InputLabelProps={{ style: { color: '#aaa' } }}
              FormHelperTextProps={{ style: { color: '#888' } }}
            />
            <TextField
              label={t('hanging.editorStudyDesc')}
              helperText={t('hanging.editorCsvHint')}
              value={studyCsv}
              onChange={(e) => patchDraft({ _studyDescCsv: e.target.value })}
              variant="outlined"
              size="small"
              style={fieldSx}
              InputProps={{ style: { color: '#fff' } }}
              InputLabelProps={{ style: { color: '#aaa' } }}
              FormHelperTextProps={{ style: { color: '#888' } }}
            />
            <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
              <TextField
                label="Rows"
                type="number"
                value={(draft.layout && draft.layout[0]) || 1}
                onChange={(e) =>
                  patchDraft({
                    layout: [
                      Number(e.target.value) || 1,
                      (draft.layout && draft.layout[1]) || 1,
                    ],
                  })
                }
                variant="outlined"
                size="small"
                style={{ width: 100 }}
                InputProps={{ style: { color: '#fff' } }}
                InputLabelProps={{ style: { color: '#aaa' } }}
              />
              <TextField
                label="Cols"
                type="number"
                value={(draft.layout && draft.layout[1]) || 1}
                onChange={(e) =>
                  patchDraft({
                    layout: [
                      (draft.layout && draft.layout[0]) || 1,
                      Number(e.target.value) || 1,
                    ],
                  })
                }
                variant="outlined"
                size="small"
                style={{ width: 100 }}
                InputProps={{ style: { color: '#fff' } }}
                InputLabelProps={{ style: { color: '#aaa' } }}
              />
            </div>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!draft.compareMode}
                  onChange={(e) => patchDraft({ compareMode: e.target.checked })}
                  color="primary"
                />
              }
              label={t('hanging.editorCompareMode')}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={draft.linkSeries !== false}
                  onChange={(e) => patchDraft({ linkSeries: e.target.checked })}
                  color="primary"
                />
              }
              label={t('hanging.editorLinkSeries')}
            />

            <div
              style={{
                marginTop: 12,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <strong style={{ fontSize: 13 }}>{t('hanging.editorSlots')}</strong>
              <Button size="small" onClick={addSlot}>
                {t('hanging.editorAddSlot')}
              </Button>
            </div>
            <p style={{ fontSize: 11, opacity: 0.75 }}>
              {t('hanging.editorRefHint')}
            </p>

            {(draft.slots || []).map((slot, i) => {
              const descCsv =
                slot._descCsv != null
                  ? slot._descCsv
                  : listToCsv((slot.series && slot.series.description) || [])
              const exclCsv =
                slot._exclCsv != null
                  ? slot._exclCsv
                  : listToCsv((slot.series && slot.series.exclude) || [])
              return (
                <div
                  key={`slot-${i}`}
                  style={{
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: 8,
                    padding: 10,
                    marginBottom: 10,
                    background: 'rgba(0,0,0,0.2)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: 6,
                    }}
                  >
                    <span style={{ fontWeight: 700, fontSize: 12 }}>
                      Slot {i} · VP {(slot.viewportIndex != null ? slot.viewportIndex : i)}
                    </span>
                    <Button size="small" onClick={() => removeSlot(i)}>
                      {t('hanging.editorRemoveSlot')}
                    </Button>
                  </div>
                  <TextField
                    select
                    label={t('hanging.editorRole')}
                    value={slot.role || 'current'}
                    onChange={(e) => patchSlot(i, { role: e.target.value })}
                    variant="outlined"
                    size="small"
                    style={fieldSx}
                    InputProps={{ style: { color: '#fff' } }}
                    InputLabelProps={{ style: { color: '#aaa' } }}
                  >
                    <MenuItem value="current">current</MenuItem>
                    <MenuItem value="prior">prior</MenuItem>
                  </TextField>
                  <TextField
                    label="viewportIndex"
                    type="number"
                    value={slot.viewportIndex != null ? slot.viewportIndex : i}
                    onChange={(e) =>
                      patchSlot(i, { viewportIndex: Number(e.target.value) || 0 })
                    }
                    variant="outlined"
                    size="small"
                    style={fieldSx}
                    InputProps={{ style: { color: '#fff' } }}
                    InputLabelProps={{ style: { color: '#aaa' } }}
                  />
                  {slot.role === 'prior' ? (
                    <TextField
                      label="pairFromSlot"
                      type="number"
                      value={
                        typeof slot.pairFromSlot === 'number' ? slot.pairFromSlot : 0
                      }
                      onChange={(e) =>
                        patchSlot(i, {
                          pairFromSlot: Number(e.target.value) || 0,
                        })
                      }
                      variant="outlined"
                      size="small"
                      style={fieldSx}
                      InputProps={{ style: { color: '#fff' } }}
                      InputLabelProps={{ style: { color: '#aaa' } }}
                    />
                  ) : null}
                  <TextField
                    label={t('hanging.editorSlotModality')}
                    value={(slot.series && slot.series.modality) || ''}
                    onChange={(e) =>
                      patchSlotSeries(i, {
                        modality: e.target.value
                          ? e.target.value.toUpperCase()
                          : null,
                      })
                    }
                    variant="outlined"
                    size="small"
                    style={fieldSx}
                    InputProps={{ style: { color: '#fff' } }}
                    InputLabelProps={{ style: { color: '#aaa' } }}
                  />
                  <TextField
                    label={t('hanging.editorInclude')}
                    value={descCsv}
                    onChange={(e) => {
                      const slots = (draft.slots || []).slice()
                      slots[i] = { ...slots[i], _descCsv: e.target.value }
                      patchDraft({ slots })
                    }}
                    variant="outlined"
                    size="small"
                    style={fieldSx}
                    InputProps={{ style: { color: '#fff' } }}
                    InputLabelProps={{ style: { color: '#aaa' } }}
                  />
                  <TextField
                    label={t('hanging.editorExclude')}
                    helperText={t('hanging.editorExcludeHint')}
                    value={exclCsv}
                    onChange={(e) => {
                      const slots = (draft.slots || []).slice()
                      slots[i] = { ...slots[i], _exclCsv: e.target.value }
                      patchDraft({ slots })
                    }}
                    variant="outlined"
                    size="small"
                    style={fieldSx}
                    InputProps={{ style: { color: '#fff' } }}
                    InputLabelProps={{ style: { color: '#aaa' } }}
                    FormHelperTextProps={{ style: { color: '#888' } }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!(slot.series && slot.series.requireDifferentPlane)}
                        onChange={(e) =>
                          patchSlotSeries(i, {
                            requireDifferentPlane: e.target.checked,
                          })
                        }
                        color="primary"
                      />
                    }
                    label={t('hanging.editorRequirePlane')}
                  />
                </div>
              )
            })}

            {error ? (
              <div style={{ color: '#ef9a9a', fontSize: 12, marginTop: 8 }}>
                {error}
              </div>
            ) : null}
          </div>
        </div>
      </DialogContent>
      <DialogActions style={{ borderTop: '1px solid rgba(255,255,255,0.12)' }}>
        <Button onClick={handleDelete} style={{ color: '#ef9a9a' }}>
          {t('hanging.editorDelete')}
        </Button>
        <div style={{ flex: 1 }} />
        <Button onClick={onClose}>{t('common.close')}</Button>
        <Button onClick={handleSave} color="primary" variant="outlined">
          {t('hanging.editorSave')}
        </Button>
        <Button onClick={handleApply} color="primary" variant="contained">
          {t('hanging.editorApply')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default HangingProtocolEditor
